import { useEffect, useMemo, useState } from "react";
import type { StoreYard } from "../types";
import { formatStoreShipToForPrint } from "../utils/poShipToPrint";

export type PoShipToChoice = { storeYardId: string };

type Props = {
  open: boolean;
  storeYards: StoreYard[];
  onCancel: () => void;
  onConfirm: (choice: PoShipToChoice) => void;
};

export function PoGenerateShipToModal({ open, storeYards, onCancel, onConfirm }: Props) {
  const [storeYardId, setStoreYardId] = useState("");

  useEffect(() => {
    if (!open) return;
    setStoreYardId(storeYards[0]?.id ?? "");
  }, [open, storeYards]);

  const preview = useMemo(() => {
    const store = storeYards.find((s) => s.id === storeYardId);
    return store ? formatStoreShipToForPrint(store) : "";
  }, [storeYardId, storeYards]);

  if (!open) return null;

  const canConfirm = Boolean(storeYardId.trim());

  return (
    <div className="po-success-overlay" role="dialog" aria-modal="true" aria-labelledby="po-ship-title">
      <button type="button" className="po-success-overlay__backdrop" aria-label="Cancel" onClick={onCancel} />
      <div
        className="card card--gov"
        style={{ position: "relative", zIndex: 2, maxWidth: "32rem", width: "100%", margin: "1rem", padding: "1.25rem" }}
      >
        <h2 id="po-ship-title" className="card__title card__title--gov" style={{ marginTop: 0 }}>
          Shipping address for PO print
        </h2>
        <p className="text-muted" style={{ fontSize: "0.875rem", marginTop: "-0.25rem" }}>
          Select a store / yard. This is used only on the printed PO (Ship to / site). It does not change issue material or
          store stock.
        </p>

        <label className="field field--full">
          <span className="field__label">Select store</span>
          <select className="form-control" value={storeYardId} onChange={(e) => setStoreYardId(e.target.value)}>
            <option value="">— Choose store —</option>
            {storeYards.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {storeYards.length === 0 ? (
            <p className="text-warning" style={{ fontSize: "0.8125rem", marginTop: "0.35rem" }}>
              No stores in master. Add a store with an address under Stores &amp; yards first.
            </p>
          ) : null}
        </label>

        <label className="field field--full">
          <span className="field__label">Address preview (on PO)</span>
          <textarea className="form-control" rows={5} readOnly value={preview} />
        </label>

        <div className="toolbar-row" style={{ marginTop: "1rem", marginBottom: 0 }}>
          <button type="button" className="btn btn--tg-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--tg"
            disabled={!canConfirm || !preview.trim() || preview === "—"}
            onClick={() => {
              if (storeYardId.trim()) onConfirm({ storeYardId: storeYardId.trim() });
            }}
          >
            Generate PO with this address
          </button>
        </div>
      </div>
    </div>
  );
}
